import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

import RegistrationForm from './components/RegistrationForm';
import Dashboard from './components/Dashboard';
import PageDetail from './components/PageDetail';
import FormBuilder from './components/FormBuilder';
import FormBuilderTest from './components/FormBuilderTest';
import FormBuilderSimple from './components/FormBuilderSimple';
import ErrorBoundary from './components/ErrorBoundary';
import PublicForm from './components/PublicForm';
import LoginPage from './components/LoginPage';
import SuperUserDashboard from './components/SuperUserDashboard';
import SocietyHomePage from './components/SocietyHomePage';
import MemberAccess from './components/MemberAccess';

function App() {
    const { auth, loading } = useAuth();

    // Debug: Log app state
    console.log('App: auth state:', auth);
    console.log('App: loading state:', loading);
    console.log('App: hostname:', window.location.hostname);

    if (loading) {
        return <div className="text-center p-10">Initializing Application...</div>;
    }

    // Check if this is the main site or a tenant subdomain
    const hostname = window.location.hostname;
    const pathname = window.location.pathname;
    
    // Extract tenant from subdomain (e.g., a.apart-frontend.com -> a)
    const tenantFromSubdomain = hostname.includes('.') && 
                               !hostname.startsWith('www') && 
                               (hostname.includes('railway.app') || hostname.includes('onrender.com')) ? 
                               hostname.split('.')[0] : null;
    
    // Extract tenant from path (e.g., /admin -> admin) - keep for backward compatibility
    const tenantFromPath = pathname.startsWith('/') && pathname.length > 1 ? 
                          pathname.split('/')[1] : null;
    
    // Determine if this is the main site (no tenant subdomain and root path)
    const isMainSite = !tenantFromSubdomain && 
                      !tenantFromPath &&  // Important: if there's a path tenant, it's NOT the main site
                      (hostname === 'localhost' || 
                       hostname.startsWith('www') || 
                       hostname === 'apartix.com' ||
                       hostname === 'superresident.com' ||
                       (hostname.includes('railway.app') && !hostname.includes('.railway.app')) ||
                       (hostname.includes('onrender.com') && !tenantFromSubdomain)) &&
                       (pathname === '/' || pathname === '');
    
    // Use subdomain tenant first, fallback to path tenant
    const currentTenant = tenantFromSubdomain || tenantFromPath;
    
    console.log('App: hostname:', hostname);
    console.log('App: tenantFromSubdomain:', tenantFromSubdomain);
    console.log('App: tenantFromPath:', tenantFromPath);
    console.log('App: currentTenant:', currentTenant);
    console.log('App: isMainSite:', isMainSite);

    return (
        <Router>
            <Routes>
                {/* Public routes accessible on any domain */}
                <Route path="/form/:publicLinkId" element={<PublicForm />} />

                {/* Routes for the main registration site */}
                {isMainSite && <Route path="*" element={<RegistrationForm />} />}

                {/* Routes for tenant subdomains or paths (e.g., a.domain.com or /admin) */}
                {currentTenant && (
                    <>
                        {/* If authenticated, show authenticated routes */}
                        {auth ? (
                            <>
                                {/* For subdomains, use root paths */}
                                {tenantFromSubdomain && <Route path="/*" element={<AuthenticatedRoutes tenantName={currentTenant} />} />}
                                {/* For path-based, keep the path structure */}
                                {!tenantFromSubdomain && tenantFromPath && <Route path={`/${tenantFromPath}/*`} element={<AuthenticatedRoutes tenantName={currentTenant} />} />}
                            </>
                        ) : (
                            /* If not authenticated, show public routes */
                            <>
                                {/* For subdomains, use root paths */}
                                {tenantFromSubdomain && (
                                    <>
                                        <Route path="/" element={<SocietyHomePage tenantName={currentTenant} />} />
                                        <Route path="/*" element={<Navigate to="/" replace />} />
                                    </>
                                )}
                                {/* For path-based, keep the path structure */}
                                {!tenantFromSubdomain && tenantFromPath && (
                                    <>
                                        <Route path={`/${tenantFromPath}`} element={<SocietyHomePage tenantName={currentTenant} />} />
                                        <Route path={`/${tenantFromPath}/*`} element={<Navigate to={`/${tenantFromPath}`} replace />} />
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}
            </Routes>
        </Router>
    );
}

function AuthenticatedRoutes({ tenantName }) {
    const { auth, logout } = useAuth();

    console.log('AuthenticatedRoutes: auth state:', auth);

    if (auth?.user?.is_superuser) {
        return (
            <div>
                <button onClick={logout}>Logout Superuser</button>
                <SuperUserDashboard />
            </div>
        );
    }

    // Use tenant name from props (path-based) or extract from hostname (subdomain-based)
    const currentTenant = tenantName || window.location.hostname.split('.')[0];

    return (
        <div>
            <div className="p-4 bg-white shadow-sm flex justify-between items-center">
                <div className="text-lg font-semibold text-gray-900">
                    Welcome, {auth?.user?.first_name || auth?.user?.username}!
                </div>
                <button onClick={logout} className="bg-gray-600 text-white px-3 py-1 rounded-md hover:bg-gray-700">
                    Logout
                </button>
            </div>
            <Routes>
                <Route path="/" element={<Dashboard tenantName={currentTenant} />} />
                <Route path="/members" element={<MemberAccess />} />
                <Route path="/page/:pageId" element={<PageDetail />} />
                <Route path="/page/:pageId/form-builder/:formId" element={<ErrorBoundary><FormBuilderSimple /></ErrorBoundary>} />
                <Route path="*" element={<Navigate to="/" />} />
            </Routes>
        </div>
    );
}

export default App;
